import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import SimpleModal from 'modules/shared/components/simple-modal';
import t from 'utils/translate';
import ContactUserItem from './contact-user-item';
import Log from 'utils/log';
import { useSelector } from 'react-redux';
import {
    getLastCalledNumber,
    getUser,
    getUserDetails,
    searchContacts,
    selectPbxDialerData,
    closePbxDialer,
} from '../stores/contacts-slice-handler';
import LocalEvent from 'utils/local-event';
import classNames from 'classnames';
import { selectCompanySettings } from '../../settings/stores/company-settings-slice-handler';
import TextInput from '../../shared/components/text-input';
import Preloader from '../../shared/components/preloader';
import SearchHighlight from '../../shared/components/search-highlight';

const PHONE_NUMBER_LENGTH_LIMIT_MAX = 15;
const PHONE_NUMBER_LENGTH_LIMIT_MIN = 3;
const DIALER_PHONE_PADS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const DIALER_PHONE_PADS_OPTIONS = {
    1: { letters: '' },
    2: { letters: 'abc' },
    3: { letters: 'def' },
    4: { letters: 'ghi' },
    5: { letters: 'jkl' },
    6: { letters: 'mno' },
    7: { letters: 'pqrs' },
    8: { letters: 'tuv' },
    9: { letters: 'wxyz' },
    '*': { letters: '' },
    0: { letters: '' },
    '#': { letters: '' },
};

const PbxDialer = () => {
    const [dataLoading, setDataLoading] = useState(false);
    const [sugestionsLoading, setSugestionsLoading] = useState(false);
    const [lastDialedNumber, setLastDialedNumber] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [internalSelectedContact, setInternalSelectedContact] = useState(null);
    const [suggestedUsersMsisdns, setSuggestedUsersMsisdns] = useState([]);
    const [padsState, setPadsState] = useState(DIALER_PHONE_PADS_OPTIONS);

    /**
     * @type {CompanySettings}
     */
    const companySettings = useSelector(selectCompanySettings);
    const pbxDialerData = useSelector(selectPbxDialerData);

    // load the last dialer number when opening
    useEffect(() => {
        if (!pbxDialerData.visible) {
            return;
        }

        setDataLoading(true);

        getLastCalledNumber().then((msisdn) => {
            setLastDialedNumber(msisdn);
            setDataLoading(false);
        });
    }, [pbxDialerData.visible]);

    useEffect(() => {
        if (!pbxDialerData.visible) {
            return;
        }

        setInternalSelectedContact(null);

        if (phoneNumber.length <= 1) {
            setSuggestedUsersMsisdns([]);
            return;
        }

        setSugestionsLoading(true);

        searchContacts('subscribers', 0, 100, {
            keyword: phoneNumber,
        }).then((contactsData) => {
            setSuggestedUsersMsisdns(contactsData.msisdns);
            setSugestionsLoading(false);
        });
    }, [phoneNumber, pbxDialerData]);

    const closeDialer = () => {
        setPhoneNumber('');
        closePbxDialer();
    };

    const handlePadClick = (value) => {
        if (phoneNumber.length === PHONE_NUMBER_LENGTH_LIMIT_MAX) return;

        setPhoneNumber(`${phoneNumber}${value}`);
    };

    const handleSuggestionClick = (msisdn) => {
        setPhoneNumber(msisdn);
        setTimeout(
            () => setInternalSelectedContact(getUser(msisdn))
        )
    };

    const handleSubmit = () => {
        if (!phoneNumber) {
            if (lastDialedNumber) {
                setPhoneNumber(lastDialedNumber);
            }

            return;
        }

        setLastDialedNumber(phoneNumber);
        
        // determine if the dialed number is external or not
        let numberIsExternal = false;
        
        const doLaunch = () => {
            
            if (numberIsExternal) {
                Log.notice('PBX DIALER', `Launching PBX cll to external number: ${phoneNumber}`);
            }

            if (pbxDialerData.callback === null) {
                LocalEvent.trigger(LocalEvent.eventTypes.CALL_OUT_TRIGGERED, {
                    phoneNumber,
                    numberIsExternal
                });
            } else {
                pbxDialerData.callback(phoneNumber, numberIsExternal);
            }
    
            setPhoneNumber('');
            setSuggestedUsersMsisdns([]);
            setDataLoading(false);
            setInternalSelectedContact(null)
    
            closeDialer();
        }
        
        if (internalSelectedContact !== null) {
            doLaunch();
        } else {
    
            setDataLoading(true);
            
            getUserDetails(phoneNumber, true).then(
                (user) => {
                    numberIsExternal = user === null;
                    doLaunch();
                }
            )
        }
    };

    if (!companySettings.AllowCallOut) {
        return '';
    }

    if (!pbxDialerData.visible) {
        return '';
    }

    /**
     * Render a user suggestion based on the given msisdn.
     * @param {string} msisdn
     */
    const renderUserSuggestion = (msisdn) => {
        return (
            <ContactUserItem
                msisdn={msisdn}
                data-test-id={`pbx-user-suggestion-${msisdn}`}
                key={`pbx-${msisdn}`}
                onClick={() => handleSuggestionClick(msisdn)}
            >
                <SearchHighlight search={phoneNumber}>{msisdn}</SearchHighlight>
            </ContactUserItem>
        );
    };

    return (
        <SimpleModal
            className="wcf-ui-pbx-dialer-dialog"
            closeCallback={() => closeDialer()}
            closeclassname="stw-cancel-close"
            dialogTitle={t('js_all_dialer')}
            data-test-id="dialer-modal"
        >
            <article className="pbx-dialer-container">
                {dataLoading && <Preloader asOverlay />}
                <section className="dialer-input-container">
                    <TextInput
                        value={phoneNumber}
                        onChange={(e) => {
                            setPhoneNumber(e.target.value.replace(new RegExp('[a-zA-Z\\s]', 'g'), ''));
                        }}
                        placeholder={t('call_out_dial')}
                    ></TextInput>

                    {phoneNumber && (
                        <span
                            onClick={() => setPhoneNumber('')}
                            className="clear-number stw-cancel"
                            data-test-id="clear-number"
                        />
                    )}
                </section>

                <section className="dialer-suggestions-list">
                    {sugestionsLoading && <Preloader asOverlay />}
                    {suggestedUsersMsisdns.map((msisdn) => renderUserSuggestion(msisdn))}
                </section>

                <section className="dialer-keyboard">
                    {DIALER_PHONE_PADS.map((key) => (
                        <div
                            className="item"
                            key={`${key}-${padsState[key].letters}`}
                            data-test-id={`touch-${key}`}
                            type="button"
                        >
                            <button
                                onClick={() => handlePadClick(key)}
                                className={classNames({
                                    label: true,
                                    active: padsState[key].clicked,
                                })}
                            >
                                {key}
                            </button>
                            <div className="letters">{padsState[key].letters}</div>
                        </div>
                    ))}
                </section>

                <footer>
                    <Button
                        className="call-out-btn"
                        onClick={handleSubmit}
                        variant="contained"
                        role="primary"
                        data-test-id="pbx-dial-button"
                        disabled={
                            dataLoading ||
                            (phoneNumber.length === 0 && lastDialedNumber === null) ||
                            (phoneNumber.length > 0 && phoneNumber.length < PHONE_NUMBER_LENGTH_LIMIT_MIN)
                        }
                    >
                        {phoneNumber.length === 0 && lastDialedNumber !== null ? (
                            <span className="stw-redial" />
                        ) : (
                            <span className="stw-call-voip" />
                        )}
                    </Button>
                </footer>
            </article>
        </SimpleModal>
    );
};

export default PbxDialer;
